import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Formik, Field, Form, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../../configs/firebase';
import { Logo } from '../../components/images';
import { FaArrowLeft, FaEnvelope, FaSpinner, FaCheckCircle } from 'react-icons/fa';

const ForgotPassword = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');

  const validationSchema = Yup.object({
    email: Yup.string()
      .email('Invalid email address')
      .required('Email is required'),
  });

  const handleSubmit = async (values, { setSubmitting, setErrors, setStatus }) => {
    try {
      setStatus(null);
      setErrors({});
      
      console.log('🔄 Sending password reset email to:', values.email);
      
      await sendPasswordResetEmail(auth, values.email, {
        url: `${window.location.origin}/login`, // Redirect back to login after reset
        handleCodeInApp: false
      });
      
      console.log('✅ Password reset email sent successfully');
      setSubmittedEmail(values.email);
      setIsSubmitted(true);
      
    } catch (error) {
      console.error('❌ Password reset error:', error);
      
      // Handle specific Firebase errors
      if (error.code === 'auth/user-not-found') {
        setErrors({
          email: 'No account found with this email address'
        });
      } else if (error.code === 'auth/invalid-email') {
        setErrors({
          email: 'Invalid email address'
        });
      } else if (error.code === 'auth/too-many-requests') {
        setErrors({
          general: 'Too many password reset attempts. Please try again later.'
        });
      } else {
        setErrors({
          general: 'Failed to send password reset email. Please try again.'
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Success state - show confirmation
  if (isSubmitted) {
    return (
      <div className='bg-gradient-to-br from-orange-200 to-orange-500 min-h-screen w-full p-4 sm:p-6 lg:p-10'>
        {/* Header */}
        <div className='flex flex-row items-center mb-8 sm:mb-12'>
          <img src={Logo} className='w-8 h-8 sm:w-10 sm:h-10' alt="Oodles Logo" />
          <p className='text-xl sm:text-2xl font-medium ml-2 text-gray-800'>
            <span className="hidden sm:inline">Oodles Internships</span>
            <span className="sm:hidden">Oodles</span>
          </p>
        </div>

        {/* Success Card */}
        <div className="flex justify-center items-center px-4">    
          <div className='w-full max-w-md'>
            <div className='bg-white/95 backdrop-blur-sm rounded-2xl shadow-xl p-6 sm:p-8 lg:p-10'>
              <div className='text-center'>
                {/* Success Icon */}
                <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-6">
                  <FaCheckCircle className="h-8 w-8 text-green-600" />
                </div>

                {/* Success Message */}
                <h1 className='text-green-600 text-2xl sm:text-3xl font-bold mb-4'>
                  Email Sent!
                </h1>
                <p className='text-gray-700 text-base sm:text-lg mb-2'>
                  We've sent a password reset link to:
                </p>
                <p className='text-orange-600 font-semibold text-lg mb-6'>
                  {submittedEmail}
                </p>
                
                <div className='text-gray-600 text-sm sm:text-base space-y-3 mb-8'>
                  <p>Check your email and click the link to reset your password.</p>
                  <p>The link will expire in 24 hours for security.</p>
                  <p className='text-xs text-gray-500'>
                    Don't see the email? Check your spam/junk folder.
                  </p>
                </div>

                {/* Action Buttons */}
                <div className='space-y-4'>
                  <Link
                    to='/login'
                    className='w-full bg-orange-500 hover:bg-orange-600 text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center'
                  >
                    <FaArrowLeft className="mr-2" />
                    Back to Sign In
                  </Link>
                  
                  <button
                    onClick={() => {
                      setIsSubmitted(false);
                      setSubmittedEmail('');
                    }}
                    className='w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-3 px-4 rounded-lg transition-colors duration-200'
                  >
                    Send Another Email
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Form state - show reset form
  return (
    <div className='bg-gradient-to-br from-orange-200 to-orange-500 min-h-screen w-full p-4 sm:p-6 lg:p-10'>
      {/* Header */}
      <div>
        <Link to="/main-signup">
            <div className='flex flex-row items-center mb-6 sm:mb-8'>
                <img src={Logo} className='w-8 h-8 sm:w-10 sm:h-10' alt="Logo"/>
                <p className='text-lg sm:text-xl lg:text-2xl ml-2 font-medium text-gray-800'>Oodles Internships</p>
            </div>
        </Link>
      </div>

      {/* Main Content */}
      <div className="flex justify-center items-center px-4">    
        <div className='w-full max-w-md'>
          {/* Reset Password Card */}
          <div className='bg-white/95 backdrop-blur-sm rounded-2xl shadow-xl p-6 sm:p-8 lg:p-10'>
            {/* Header */}
            <div className='text-center mb-8'>
              <h1 className='text-orange-500 text-2xl sm:text-3xl lg:text-4xl font-bold mb-2'>
                Reset Password
              </h1>
              <p className='text-gray-600 text-sm sm:text-base'>
                Enter your email address and we'll send you a link to reset your password
              </p>
            </div>

            <Formik
              initialValues={{ email: '' }}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({ errors, touched, isSubmitting }) => (
                <Form className='space-y-6'>
                  {/* General Error Message */}
                  {errors.general && (
                    <div className='bg-red-50 border border-red-200 rounded-lg p-4'>
                      <p className='text-red-800 text-sm font-medium'>
                        {errors.general}
                      </p>
                    </div>
                  )}

                  {/* Email Field */}
                  <div>
                    <label htmlFor="email" className='block text-sm font-medium text-gray-700 mb-2'>
                      Email Address
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <FaEnvelope className="h-5 w-5 text-gray-400" />
                      </div>
                      <Field
                        type="email"
                        name="email"
                        className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors ${
                          errors.email && touched.email 
                            ? 'border-red-300 bg-red-50' 
                            : 'border-gray-300'
                        }`}
                        placeholder="Enter your email address"
                        disabled={isSubmitting}
                      />
                    </div>
                    <ErrorMessage 
                      name="email" 
                      component="div" 
                      className="mt-1 text-sm text-red-600" 
                    />
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className='w-full bg-orange-500 hover:bg-orange-600 disabled:bg-orange-300 text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center'
                  >
                    {isSubmitting ? (
                      <>
                        <FaSpinner className="animate-spin -ml-1 mr-3 h-5 w-5" />
                        Sending Reset Link...
                      </>
                    ) : (
                      <>
                        <FaEnvelope className="mr-2" />
                        Send Reset Link
                      </>
                    )}
                  </button>

                  {/* Navigation Links */}
                  <div className='space-y-4 pt-4 border-t'>
                    <div className='text-center'>
                      <Link 
                        to='/main-signup' 
                        className='text-sm text-gray-500 hover:text-gray-700 flex items-center justify-center'
                      >
                        <FaArrowLeft className="mr-2" />
                        Back to main page
                      </Link>
                    </div>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;